<script setup lang="ts">
import { Eye, EyeOff } from 'lucide-vue-next';
import { ref, type HTMLAttributes } from 'vue';
import { useI18n } from 'vue-i18n';

import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface Props {
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  id?: string;
  autocomplete?: string;
  class?: HTMLAttributes['class'];
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: '',
  disabled: false,
  error: '',
  id: '',
  autocomplete: undefined,
  class: undefined,
});

const emit = defineEmits<{
  focusout: [];
}>();

const model = defineModel<string>({ default: '' });

const { t } = useI18n();
const passwordVisible = ref(false);

function toggleVisibility(): void {
  passwordVisible.value = !passwordVisible.value;
}

function handleFocusOut(event: FocusEvent): void {
  const container = event.currentTarget as HTMLElement;
  const relatedTarget = event.relatedTarget as HTMLElement | null;

  if (relatedTarget && container.contains(relatedTarget)) {
    return;
  }

  emit('focusout');
}
</script>

<template>
  <div class="w-full">
    <div class="relative" @focusout="handleFocusOut">
      <Input
        :id="id"
        v-model="model"
        :type="passwordVisible ? 'text' : 'password'"
        :placeholder="placeholder"
        :disabled="disabled"
        :autocomplete="autocomplete"
        :aria-invalid="!!error || undefined"
        :class="cn('pr-10', props.class)"
      />
      <button
        type="button"
        tabindex="-1"
        :disabled="disabled"
        :aria-label="
          passwordVisible ? t('common.labels.hidePassword') : t('common.labels.showPassword')
        "
        class="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex items-center pr-3 disabled:pointer-events-none disabled:opacity-50"
        @click="toggleVisibility"
      >
        <Eye v-if="passwordVisible" class="h-4 w-4" />
        <EyeOff v-else class="h-4 w-4" />
      </button>
    </div>
    <p v-if="error" class="text-destructive mt-1 text-sm">
      {{ error }}
    </p>
  </div>
</template>
